import { useRef, useState } from "react";

interface PreviewVoice {
  id: string;
  previewUrl?: string;
}

export const RING_SIZE = 34;
export const RING_STROKE = 2.5;
export const RING_R = (RING_SIZE - RING_STROKE) / 2;
export const RING_CIRC = 2 * Math.PI * RING_R;

export function useVoicePreview() {
  const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(
    null
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const activeIdRef = useRef<string | null>(null);
  const rafRef = useRef<number | null>(null);
  const arcRef = useRef<SVGCircleElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);

  const stopRaf = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const stopAudio = () => {
    stopRaf();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onplaying = null;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.src = "";
      audioRef.current = null;
    }
    activeIdRef.current = null;
    setPreviewingVoiceId(null);
    if (arcRef.current) {
      arcRef.current.style.strokeDashoffset = String(RING_CIRC);
    }
    if (barRef.current) {
      barRef.current.style.width = "0%";
    }
  };

  const handleVoicePreview = (voice: PreviewVoice, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!voice.previewUrl) return;

    stopRaf();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onplaying = null;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.src = "";
      audioRef.current = null;
    }

    if (activeIdRef.current === voice.id) {
      activeIdRef.current = null;
      setPreviewingVoiceId(null);
      return;
    }

    activeIdRef.current = voice.id;
    setPreviewingVoiceId(voice.id);
    if (arcRef.current) {
      arcRef.current.style.strokeDashoffset = String(RING_CIRC);
    }

    const audio = new Audio(voice.previewUrl);
    audioRef.current = audio;

    audio.onplaying = () => {
      const tick = () => {
        if (audio.duration) {
          const progress = audio.currentTime / audio.duration;
          if (arcRef.current) {
            arcRef.current.style.strokeDashoffset = String(
              RING_CIRC * (1 - progress)
            );
          }
          if (barRef.current) {
            barRef.current.style.width = `${progress * 100}%`;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const stop = () => stopAudio();
    audio.onended = stop;
    audio.onerror = stop;
    audio.play().catch(stop);
  };

  return {
    previewingVoiceId,
    arcRef,
    barRef,
    handleVoicePreview,
    stopPreview: stopAudio,
    ringSize: RING_SIZE,
    ringStroke: RING_STROKE,
    ringR: RING_R,
    ringCirc: RING_CIRC,
  };
}
